import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { legalEntitiesApi } from '@/api/legalEntities'
import { cabinetsApi } from '@/api/cabinets'
import { categoriesApi } from '@/api/categories'
import { productGroupsApi } from '@/api/productGroups'
import { suppliersApi } from '@/api/suppliers'
import { productsApi } from '@/api/products'
import { scenariosApi } from '@/api/scenarios'
import { salesPlansApi } from '@/api/salesPlans'
import { budgetEntriesApi } from '@/api/budgetEntries'
import { loansApi } from '@/api/loans'
import { getErrorMessage } from '@/utils/error'
import type { PlanningScenario } from '@/types'

type OnboardingCounts = {
  legalEntities: number
  cabinets: number
  categories: number
  productGroups: number
  suppliers: number
  products: number
  productsWithCategory: number
  scenarios: number
}

type ScenarioCounts = {
  salesPlans: number
  budgetEntries: number
  loans: number
}

function getForcedCompleteKey(userId: number): string {
  return `onboarding:forcedComplete:${userId}`
}

export const useOnboardingStore = defineStore('onboarding', () => {
  const loading = ref(false)
  const error = ref<string | null>(null)

  const currentUserId = ref<number | null>(null)
  const forcedComplete = ref(false)
  const lastLoadedAtMs = ref<number | null>(null)

  const counts = ref<OnboardingCounts>({
    legalEntities: 0,
    cabinets: 0,
    categories: 0,
    productGroups: 0,
    suppliers: 0,
    products: 0,
    productsWithCategory: 0,
    scenarios: 0,
  })

  const scenarios = ref<PlanningScenario[]>([])
  const selectedScenarioId = ref<number | null>(null)
  const scenarioCounts = ref<ScenarioCounts>({
    salesPlans: 0,
    budgetEntries: 0,
    loans: 0,
  })

  const step1Completed = computed(() => counts.value.legalEntities > 0)
  const step2Completed = computed(() => counts.value.cabinets > 0)
  const step3Completed = computed(() => {
    return (
      counts.value.categories > 0 &&
      counts.value.productGroups > 0 &&
      counts.value.suppliers > 0
    )
  })
  const step4Completed = computed(() => counts.value.productsWithCategory > 0)
  const step5ScenarioCreated = computed(() => counts.value.scenarios > 0)
  const step5PlansCompleted = computed(() => scenarioCounts.value.salesPlans > 0)
  const step5ExpensesCompleted = computed(() => scenarioCounts.value.budgetEntries > 0)
  const step5LoansCompleted = computed(() => scenarioCounts.value.loans > 0)
  const step5Completed = computed(() => {
    return (
      step5ScenarioCreated.value &&
      step5PlansCompleted.value &&
      step5ExpensesCompleted.value &&
      step5LoansCompleted.value
    )
  })

  const isSoftCompleted = computed(() => {
    return (
      step1Completed.value &&
      step2Completed.value &&
      step3Completed.value &&
      step4Completed.value &&
      step5Completed.value
    )
  })

  const isCompleted = computed(() => forcedComplete.value || isSoftCompleted.value)

  function loadForcedComplete(userId: number): boolean {
    try {
      return localStorage.getItem(getForcedCompleteKey(userId)) === '1'
    } catch {
      return false
    }
  }

  function saveForcedComplete(userId: number, value: boolean): void {
    try {
      if (value) {
        localStorage.setItem(getForcedCompleteKey(userId), '1')
      } else {
        localStorage.removeItem(getForcedCompleteKey(userId))
      }
    } catch {
      return
    }
  }

  function setForcedComplete(value: boolean): void {
    if (!currentUserId.value) {
      forcedComplete.value = value
      return
    }
    forcedComplete.value = value
    saveForcedComplete(currentUserId.value, value)
  }

  function setSelectedScenarioId(value: number | null): void {
    selectedScenarioId.value = value
  }

  async function initForUser(userId: number): Promise<void> {
    if (currentUserId.value !== userId) {
      currentUserId.value = userId
      forcedComplete.value = loadForcedComplete(userId)
      lastLoadedAtMs.value = null
      selectedScenarioId.value = null
      scenarios.value = []
      scenarioCounts.value = { salesPlans: 0, budgetEntries: 0, loans: 0 }
      counts.value = {
        legalEntities: 0,
        cabinets: 0,
        categories: 0,
        productGroups: 0,
        suppliers: 0,
        products: 0,
        productsWithCategory: 0,
        scenarios: 0,
      }
    } else {
      forcedComplete.value = loadForcedComplete(userId)
    }
  }

  async function refreshBaseCounts(): Promise<void> {
    loading.value = true
    error.value = null

    try {
      const [
        legalEntities,
        cabinets,
        categories,
        productGroups,
        suppliers,
        products,
        scenariosList,
      ] = await Promise.all([
        legalEntitiesApi.getAll(),
        cabinetsApi.getAll({ limit: 100 }),
        categoriesApi.getAll({ limit: 100 }),
        productGroupsApi.getAll({ limit: 100 }),
        suppliersApi.getAll({ limit: 100 }),
        productsApi.getAll({ limit: 100 }),
        scenariosApi.getAll({ limit: 50, include_archived: false }),
      ])

      const productsWithCategory = products.filter((p: any) => p.category_id !== null)

      counts.value = {
        legalEntities: legalEntities.length,
        cabinets: cabinets.length,
        categories: categories.length,
        productGroups: productGroups.length,
        suppliers: suppliers.length,
        products: products.length,
        productsWithCategory: productsWithCategory.length,
        scenarios: scenariosList.length,
      }

      scenarios.value = scenariosList

      if (selectedScenarioId.value === null) {
        const active = scenariosList.find((s: PlanningScenario) => s.status === 'active')
        selectedScenarioId.value = active?.id ?? scenariosList[0]?.id ?? null
      } else if (!scenariosList.some((s: PlanningScenario) => s.id === selectedScenarioId.value)) {
        selectedScenarioId.value = scenariosList[0]?.id ?? null
      }

      lastLoadedAtMs.value = Date.now()
    } catch (e) {
      error.value = getErrorMessage(e, 'Не удалось загрузить прогресс онбординга')
      throw e
    } finally {
      loading.value = false
    }
  }

  async function refreshScenarioCounts(): Promise<void> {
    const scenarioId = selectedScenarioId.value
    if (!scenarioId) {
      scenarioCounts.value = { salesPlans: 0, budgetEntries: 0, loans: 0 }
      return
    }

    loading.value = true
    error.value = null
    try {
      const [salesPlans, budgetEntries, loans] = await Promise.all([
        salesPlansApi.getAll({ scenario_id: scenarioId }),
        budgetEntriesApi.getAll({ scenario_id: scenarioId, limit: 1 }),
        loansApi.getAll({ scenario_id: scenarioId, limit: 1 }),
      ])

      scenarioCounts.value = {
        salesPlans: salesPlans.length,
        budgetEntries: budgetEntries.length,
        loans: loans.length,
      }
    } catch (e) {
      error.value = getErrorMessage(e, 'Не удалось загрузить данные сценария для онбординга')
      throw e
    } finally {
      loading.value = false
    }
  }

  async function refreshAll(): Promise<void> {
    await refreshBaseCounts()
    await refreshScenarioCounts()
  }

  async function ensureFresh(maxAgeMs: number): Promise<void> {
    if (forcedComplete.value) return
    const last = lastLoadedAtMs.value
    if (last !== null && Date.now() - last < maxAgeMs) return
    await refreshAll()
  }

  function shouldRedirectToOnboarding(path: string): boolean {
    if (forcedComplete.value) return false
    if (isSoftCompleted.value) return false
    const normalized =
      path === '/app'
        ? '/'
        : path.startsWith('/app/')
          ? path.slice('/app'.length)
          : path

    if (normalized === '/onboarding') return false
    if (normalized === '/help/faq') return false
    if (normalized.startsWith('/billing/')) return false
    if (normalized === '/settings' || normalized.startsWith('/settings/')) return false
    if (normalized === '/terms' || normalized === '/privacy') return false
    if (
      normalized === '/products' ||
      normalized === '/planning' ||
      normalized === '/planning-page'
    ) {
      return false
    }
    return true
  }

  return {
    loading,
    error,
    forcedComplete,
    counts,
    scenarios,
    selectedScenarioId,
    scenarioCounts,
    step1Completed,
    step2Completed,
    step3Completed,
    step4Completed,
    step5ScenarioCreated,
    step5PlansCompleted,
    step5ExpensesCompleted,
    step5LoansCompleted,
    step5Completed,
    isSoftCompleted,
    isCompleted,
    initForUser,
    refreshAll,
    refreshScenarioCounts,
    ensureFresh,
    shouldRedirectToOnboarding,
    setForcedComplete,
    setSelectedScenarioId,
  }
})
